/*
 * SVG Editor
 * version: 1.0.1
 *
 */

import React, { useState, useEffect } from 'react';

import Notification from './components/Notification';
import Header from './components/Header';
import Button from './components/Button';
import FloatingMenu from './components/FloatingMenu';
import Menu from './components/Menu';
import Toolbar from './components/Toolbar';
import FabricCanvas from './components/FabricCanvas';
import ToolPanel from './components/ToolPanel';
import SelectionSettings from './components/SelectionSettings';
import CanvasSettings from './components/CanvasSettings';
import DrawSettings from './components/DrawSettings';
import Shapes from './components/Shapes';
import UploadSettings from './components/UploadSettings';
import IntegrationSettings from './components/IntegrationSettings';

import __ from './utils/translation';
import saveInBrowser from './utils/saveInBrowser';
import { downloadImage, downloadSVG } from './utils/downloadImage';
import { undo, redo } from './utils/undoRedo';
import { editorTips } from './utils/editorTips';
import { handleDrawingModes } from './utils/handleDrawingModes';
import { applyZoom, zoomWithKeys, zoomWithMouse } from './utils/zoom';

import logo from './images/logo.png';
import { ReactComponent as IconGear } from './icons/gear.svg';
import { ReactComponent as IconUndo } from './icons/undo.svg';
import { ReactComponent as IconRedo } from './icons/redo.svg';
import { ReactComponent as IconTick } from './icons/tick.svg';
import { ReactComponent as IconDownload } from './icons/down.svg';
import { ReactComponent as IconClose } from './icons/close.svg';
import { ReactComponent as IconBrush } from './icons/brush.svg';
import { ReactComponent as IconCursor } from './icons/cursor.svg';
import { ReactComponent as IconLine } from './icons/line.svg';
import { ReactComponent as IconPath } from './icons/path.svg';
import { ReactComponent as IconShape } from './icons/shape.svg';
import { ReactComponent as IconText } from './icons/text.svg';
import { ReactComponent as IconUpload } from './icons/upload.svg';
import { ReactComponent as IconZoom } from './icons/zoom.svg';



const App = () => {

  // states
  const [notification, setNotification] = useState({ message: null, type: null, seconds: null })
  const [downloadMenuVisible, setDownloadMenuVisible] = useState(false)
  const [activeTool, setActiveTool] = useState('select')

  const [canvas, setCanvas] = useState()
  const [loadSavedCanvas, setLoadSavedCanvas] = useState(true)
  const [activeSelection, setActiveSelection] = useState(null)
  const [history, setHistory] = useState({ index: null, states: [] })
  const [selectionInfo, setSelectionInfo] = useState(editorTips[Math.floor(Math.random() * editorTips.length)])
  const [zoom, setZoom] = useState(1)


  //--------------------------------------------------------------------


  // on start: check if there is a saved canvas in this browser and ask if we should load it
  useEffect(() => {
    if (canvas && loadSavedCanvas) {
      Promise.resolve(saveInBrowser.loadServer('canvasEditor')).then((res) => {
        if (res) {
          canvas.loadFromJSON(res, canvas.renderAll.bind(canvas));
        }
      });
      setLoadSavedCanvas(false);
    }
  }, [canvas, loadSavedCanvas])


  //--------------------------------------------------------------------


  // on active selection update: change active tool to select
  useEffect(() => {
    if (!activeSelection) return

    setActiveTool('select')

    // scroll to top in tool panel
    document.querySelector('.toolpanel .holder').scrollTop = 0
  }, [activeSelection])


  //--------------------------------------------------------------------


  // on active tool change: deselect all object, handle drawing modes
  useEffect(() => {
    if (!canvas) return

    if (activeTool !== 'select') canvas.discardActiveObject().requestRenderAll()

    handleDrawingModes(canvas, activeTool, setSelectionInfo)
  }, [canvas, activeTool])


  //--------------------------------------------------------------------


  // save history and unsaved work alert
  const maxHistory = 10
  useEffect(() => {
    if (!canvas) return

    const saveHistory = () => {
      let updatedHistory = [...history.states]

      // if any action happens after undo, clear all (redo) actions after current state
      if (history.index < history.states.length - 1) updatedHistory.splice(history.index + 1)

      // add current state to history
      updatedHistory.push(canvas.toJSON())
      if (updatedHistory.length > maxHistory) updatedHistory.shift()

      setHistory({ index: updatedHistory.length - 1, states: updatedHistory })
    }
    canvas.on('object:modified', saveHistory)
    canvas.on('path:created', saveHistory)


    const unsavedWorkAlert = (e) => {
      if (history.states.length > 1) e.returnValue = __(`Are you sure you want to leave?`)
    }
    window.addEventListener('beforeunload', unsavedWorkAlert)


    // cleanup
    return () => {
      canvas.off('object:modified', saveHistory)
      canvas.off('path:created', saveHistory)

      window.removeEventListener('beforeunload', unsavedWorkAlert)
    }
  }, [canvas, history])


  //--------------------------------------------------------------------


  // keyboard & mouse shortcuts
  useEffect(() => {
    if (!canvas) return

    // select tool (v)
    const keyV = (e) => {
      const key = e.which || e.keyCode;
      if (key === 86 && document.querySelectorAll('textarea:focus, input:focus').length === 0) {
        setActiveTool('select')
      }
    }
    document.addEventListener('keydown', keyV)


    // undo/redo (ctrl z/y)
    const ctrZY = (e) => {
      const key = e.which || e.keyCode;

      if (key === 90 && e.ctrlKey && document.querySelectorAll('textarea:focus, input:focus').length === 0) {
        undo(canvas, history, setHistory)
      }

      if (key === 89 && e.ctrlKey && document.querySelectorAll('textarea:focus, input:focus').length === 0) {
        redo(canvas, history, setHistory)
      }
    }
    document.addEventListener('keydown', ctrZY)


    // zoom out/in/reset (ctr + -/+/0)
    const keyZoom = (e) => zoomWithKeys(e, canvas, setZoom, applyZoom)
    document.addEventListener('keydown', keyZoom)


    // zoom out/in with mouse
    const mouseZoom = (e) => zoomWithMouse(e, canvas, setZoom, applyZoom)
    document.addEventListener('wheel', mouseZoom, { passive: false })


    // cleanup
    return () => {
      document.removeEventListener('keydown', keyV)
      document.removeEventListener('keydown', ctrZY)
      document.removeEventListener('keydown', keyZoom)
      document.removeEventListener('wheel', mouseZoom)
    }
  }, [canvas, history])


  //--------------------------------------------------------------------


  // render layout
  return (
    <div id="app">

      <Notification notification={notification} setNotification={setNotification} />


      <Header logo={logo}>
        <Button title={__('Undo')} handleClick={() => undo(canvas, history, setHistory)}
          className={(!history.index || history.index === 0) ? 'disabled' : ''}><IconUndo /></Button>
        <Button title={__('Redo')} handleClick={() => redo(canvas, history, setHistory)}
          className={(history.index < (history.states.length - 1)) ? '' : 'disabled'}><IconRedo /></Button>

        <div className="separator"></div>
        {/* <Button title={__('Save (to Server)')} handleClick={() => {
          saveInBrowser.saveServer('canvasEditor', canvas.toJSON(), canvas.toDataURL());
          setNotification({ message: __('Project is load from server!'), seconds: 3 })
        }}><IconTick /></Button> */}
        {/* <Button title={__('Save (to browser)')} handleClick={() => {
          saveInBrowser.save('canvasEditor', canvas.toJSON());
          setNotification({ message: __('Project is saved in this browser!'), seconds: 3 })
        }}><IconTick /></Button> */}
        <Button title={__('Download as..')} handleClick={() => setDownloadMenuVisible(!downloadMenuVisible)}><IconDownload /></Button>
        <Button title={__('Close and open new')} handleClick={() => {
          if (window.confirm(__('This will clear the canvas! Are you sure?'))) {
            setHistory({ index: null, states: [] }); canvas.clear(); saveInBrowser.remove('canvasEditor');
          }
        }} className="close"><IconClose /></Button>

        <FloatingMenu visible={downloadMenuVisible} setVisible={setDownloadMenuVisible}>
          <Menu handleClick={() => { setDownloadMenuVisible(false); downloadSVG(canvas.toSVG()); }}>{__('Download as SVG')}</Menu>
          <Menu handleClick={() => { setDownloadMenuVisible(false); downloadImage(canvas.toDataURL()); }}>{__('Download as PNG')}</Menu>
          <Menu handleClick={() => {
            setDownloadMenuVisible(false);
            downloadImage(canvas.toDataURL({ format: 'jpeg' }), 'jpg', 'image/jpeg');
          }}>{__('Download as JPG')}</Menu>
        </FloatingMenu>
      </Header>


      <Toolbar activeTool={activeTool}>
        <Button name="select" title={__('Select/move object (V)')} handleClick={() => setActiveTool('select')}><IconCursor /></Button>
        <Button name="shapes" title={__('Shapes')} handleClick={() => setActiveTool('shapes')}><IconShape /></Button>
        <Button name="line" title={__('Line')} handleClick={() => setActiveTool('line')}><IconLine /></Button>
        <Button name="path" title={__('Connectable lines & curves')} handleClick={() => setActiveTool('path')}><IconPath /></Button>
        <Button name="draw" title={__('Free draw')} handleClick={() => setActiveTool('draw')}><IconBrush /></Button>
        <Button name="textbox" title={__('Text box')} handleClick={() => setActiveTool('textbox')}><IconText /></Button>
        <Button name="upload" title={__('Upload image')} handleClick={() => setActiveTool('upload')}><IconUpload /></Button>
        <Button name="Integrations" title={__('Search image')} handleClick={() => setActiveTool('integration')}>
        <svg height="136pt" viewBox="-1 0 136 136.21852" width="136pt"><path d="M 93.148438 80.832031 C 109.5 57.742188 104.03125 25.769531 80.941406 9.421875 C 57.851562 -6.925781 25.878906 -1.460938 9.53125 21.632812 C -6.816406 44.722656 -1.351562 76.691406 21.742188 93.039062 C 38.222656 104.707031 60.011719 105.605469 77.394531 95.339844 L 115.164062 132.882812 C 119.242188 137.175781 126.027344 137.347656 130.320312 133.269531 C 134.613281 129.195312 134.785156 122.410156 130.710938 118.117188 C 130.582031 117.980469 130.457031 117.855469 130.320312 117.726562 Z M 51.308594 84.332031 C 33.0625 84.335938 18.269531 69.554688 18.257812 51.308594 C 18.253906 33.0625 33.035156 18.269531 51.285156 18.261719 C 69.507812 18.253906 84.292969 33.011719 84.328125 51.234375 C 84.359375 69.484375 69.585938 84.300781 51.332031 84.332031 C 51.324219 84.332031 51.320312 84.332031 51.308594 84.332031 Z M 51.308594 84.332031"></path></svg>
          </Button>
        <div className="separator"></div>
        <Button name="background" title={__('Canvas options')} handleClick={() => setActiveTool('background')}><IconGear /></Button>
      </Toolbar>


      <ToolPanel visible={activeSelection || (
        activeTool !== 'select' && activeTool !== 'line' && activeTool !== 'path' && activeTool !== 'textbox'
      )}>

        {activeSelection && <SelectionSettings canvas={canvas} activeSelection={activeSelection} />}

        {activeTool === 'background' && !activeSelection && <CanvasSettings canvas={canvas} />}

        {activeTool === 'draw' && !activeSelection && <DrawSettings canvas={canvas} />}

        {activeTool === 'shapes' && !activeSelection && <Shapes canvas={canvas} />}

        {activeTool === 'upload' && !activeSelection && <UploadSettings canvas={canvas} />}
        {activeTool === 'integration' && !activeSelection && <IntegrationSettings canvas={canvas} />}
      </ToolPanel>


      <FabricCanvas canvas={canvas} setCanvas={setCanvas}
        selectionInfo={selectionInfo} setSelectionInfo={setSelectionInfo}
        setActiveSelection={setActiveSelection}
        setHistory={setHistory} />


      <div className="bottom-info">
        <IconZoom />
        <select onChange={(e) => { setZoom(e.target.value); applyZoom(canvas, e.target.value); }} value={zoom}>
          <option>{parseInt(zoom * 100)}%</option>
          <option value="0.05">5%</option><option value="0.1">10%</option>
          <option value="0.25">25%</option><option value="0.5">50%</option>
          <option value="0.75">75%</option><option value="1">100%</option>
          <option value="1.5">150%</option><option value="2">200%</option>
          <option value="2.5">250%</option><option value="3">300%</option>
        </select>
      </div>

    </div>
  )

}


export default App
